import Template from '../../component/Template.js';
import React, { useEffect, useRef, useState } from 'react';
import Api from '../../component/Api';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../component/useAuth.js';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PDFModal from '../../component/PDFModal.js';
import 'datatables.net';
import Swal from 'sweetalert2';
import 'datatables.net-bs4/css/dataTables.bootstrap4.min.css';
import ViewApplicationModal from '../../component/ExternalCredit/ViewExternalCreditApplication.js';
import useViewApplicationModal from '../../component/ExternalCredit/useViewApplication.js';

import Select from 'react-select';
import $ from 'jquery';

const ActionBtn = ({ onClick, title, borderColor, hoverBg, hoverColor, icon, baseColor }) => {
    const base = {
        background: '#fff',
        border: `1.5px solid ${borderColor}`,
        color: baseColor || borderColor,
        borderRadius: '7px',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        flexShrink: 0,
    };

    return (
        <button
            type="button"
            title={title}
            style={base}
            onMouseEnter={e => {
                e.currentTarget.style.background = hoverBg || borderColor;
                e.currentTarget.style.color = hoverColor || '#fff';
                e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = baseColor || borderColor;
                e.currentTarget.style.transform = 'scale(1)';
            }}
            onClick={onClick}
        >
            <i className={icon} style={{ fontSize: '15px', pointerEvents: 'none' }}></i>
        </button>
    );
};

export default function BOSExternalCreditApprovalList() {

    const navigate = useNavigate();
    const isAuth = useAuth();

    if (isAuth === false) {
        navigate("/college/");
    }

    const tableRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const { http } = Api();

    // Data states
    const [studentData, setStudentData] = useState([]);
    const [getCollegesData, setCollegesData] = useState([]);
    const [getDisciplinesData, setDisciplinesData] = useState([]);

    // Filter states
    const [selectedAdmissionYearID, setSelectedAdmissionYearID] = useState('');
    const [selectedAdmissionYear, setSelectedAdmissionYear] = useState(null);
    const [changedCollegeData, setChangedCollegeData] = useState(null);
    const [changedCollegesId, setChangedCollegesId] = useState(0);
    const [changedDisciplineData, setChangedDisciplineData] = useState(null);
    const [changedDisciplineId, setChangedDisciplineId] = useState(0);

    // Tab state (fixed to BOS content only)
    const [activeTab, setActiveTab] = useState('forwarded_to_bos');

    // Selection states for bulk actions
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Custom datatable states
    const [sortBy, setSortBy] = useState({ column: null, direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredAndSortedData, setFilteredAndSortedData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total_count, setTotal] = useState(0);

    const customStyles = {
        menu: (provided) => ({
            ...provided,
            maxHeight: '200px',
            overflowY: 'auto',
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: '200px',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: '#888 #f1f1f1',
        }),
    };

    // Fetch colleges when admission year is selected
    useEffect(() => {
        const fetchColleges = async () => {
            if (!selectedAdmissionYearID) {
                return;
            }

            try {
                const collegeResponse = await http.get('/College/getACA16ExternalCreditColleges', {
                    params: {
                        admissionyear: selectedAdmissionYearID
                    }
                });

                if (collegeResponse.data.colleges) {
                    setCollegesData(collegeResponse.data.colleges);
                }
            } catch (error) {
                if (error.response?.data?.http_status === 403 && error.response.data.status_code === "ERR37") {
                    toast.error("Invalid Token");
                    setTimeout(() => {
                        navigate("/admin/");
                    }, 5000);
                } else {
                    toast.error(error.response?.data?.message || "Error fetching colleges");
                }
            }
        };

        fetchColleges();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAdmissionYearID]); // Removed http & navigate to fix the infinite loop

    // Fetch disciplines when college is selected
    useEffect(() => {
        const fetchDisciplines = async () => {
            if (!selectedAdmissionYearID || !changedCollegesId) {
                return;
            }

            try {
                const disciplineResponse = await http.get('/College/getACA16ExternalCreditDisciplines', {
                    params: {
                        college_id: changedCollegesId,
                        admissionyear: selectedAdmissionYearID
                    }
                });

                if (disciplineResponse.data.disciplines) {
                    setDisciplinesData(disciplineResponse.data.disciplines);
                }
            } catch (error) {
                if (error.response?.data?.http_status === 403 && error.response.data.status_code === "ERR37") {
                    toast.error("Invalid Token");
                    setTimeout(() => {
                        navigate("/admin/");
                    }, 5000);
                } else {
                    toast.error(error.response?.data?.message || "Error fetching disciplines");
                }
            }
        };

        fetchDisciplines();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changedCollegesId, selectedAdmissionYearID]); // Removed http & navigate to fix the infinite loop

    // Filter data for BOS stack only
    const getFilteredDataByTab = () => {
        return studentData.filter(student =>
            ['forwarded_to_bos', 'bos_course_approved', 'application_approved'].includes(student.application_status)
        );
    };

    // Handle search and sorting
    useEffect(() => {
        const tabFilteredData = getFilteredDataByTab();

        const filtered = [...tabFilteredData].filter(student => {
            return (
                student.prn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.studentname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.apaar_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.course_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                student.discipline_name?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }).sort((a, b) => {
            if (!sortBy.column) return 0;

            if (sortBy.direction === 'asc') {
                return a[sortBy.column] > b[sortBy.column] ? 1 : -1;
            } else {
                return a[sortBy.column] < b[sortBy.column] ? 1 : -1;
            }
        });

        const itemsPerPage = 50;
        const startIndex = (currentPage - 1) * itemsPerPage;
        const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage);

        setFilteredAndSortedData(paginatedData);
        setTotalPages(Math.ceil(filtered.length / itemsPerPage));
        setTotal(filtered.length);
    }, [searchQuery, sortBy, studentData, currentPage, activeTab]);

    const handleAdmissionYearChange = (selectedOption) => {
        setSelectedAdmissionYearID(selectedOption.value);
        setSelectedAdmissionYear(selectedOption);
        // Reset dependent fields
        setChangedCollegeData(null);
        setChangedCollegesId(0);
        setChangedDisciplineData(null);
        setChangedDisciplineId(0);
        setCollegesData([]);
        setDisciplinesData([]);
        setStudentData([]);
        setSelectedStudents([]);
        setSelectAll(false);
    };

    const handleCollegeChange = (selectedOption) => {
        setChangedCollegeData(selectedOption);
        setChangedCollegesId(selectedOption.value);
        // Reset dependent fields
        setChangedDisciplineData(null);
        setChangedDisciplineId(0);
        setDisciplinesData([]);
        setStudentData([]);
        setSelectedStudents([]);
        setSelectAll(false);
    };

    const handleDisciplineChange = (selectedOption) => {
        setChangedDisciplineData(selectedOption);
        setChangedDisciplineId(selectedOption.value);
        // Clear student data when discipline changes
        setStudentData([]);
        setSelectedStudents([]);
        setSelectAll(false);
    };

    const handleClearFields = () => {
        setSelectedAdmissionYearID('');
        setSelectedAdmissionYear(null);
        setChangedCollegeData(null);
        setChangedCollegesId(0);
        setChangedDisciplineData(null);
        setChangedDisciplineId(0);
        setCollegesData([]);
        setDisciplinesData([]);
        setStudentData([]);
        setSelectedStudents([]);
        setSelectAll(false);
    };

    // Fetch student list only when Go button is clicked
    const showStudentList = async () => {
        if (!selectedAdmissionYearID || !changedCollegesId || !changedDisciplineId) {
            toast.error("Please select Admission Year, College and Discipline");
            return;
        }

        setLoading(true);
        setStudentData([]);
        setSelectedStudents([]);
        setSelectAll(false);

        try {
            const response = await http.get('/College/getBOSExternalCreditList', {
                params: {
                    admissionyear: selectedAdmissionYearID,
                    college_id: changedCollegesId,
                    discipline_id: changedDisciplineId
                }
            });

            if (response.data.students && response.data.students.length > 0) {
                setStudentData(response.data.students);
            } else if (response.data.status_code === "ERR5") {
                setStudentData([]);
                toast.error("Data not found");
            } else {
                setStudentData([]);
            }

        } catch (error) {
            if (error.response?.data?.http_status === 403 && error.response.data.status_code === "ERR37") {
                toast.error("Invalid Token");
                setTimeout(() => {
                    navigate("/admin/");
                }, 5000);
            } else if (error.response?.data?.http_status === 429 && error.response.data.status_code === "ERR33") {
                toast.error(error.response.data.message);
            } else {
                toast.error(error.response?.data?.message || "Error fetching data");
            }
        } finally {
            setLoading(false);
        }
    };

    const {
        showViewModal,
        viewData,
        viewLoading,
        pdfLoading,
        pdfModal,
        openViewModal,
        closeViewModal,
        openPdfModal,
        closePdfModal,
    } = useViewApplicationModal(http);

    const handleSearchInputChange = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        const newSortBy = {
            column,
            direction: sortBy.column === column && sortBy.direction === 'asc' ? 'desc' : 'asc',
        };
        setSortBy(newSortBy);
    };

    const renderSortIcon = (columnName) => {
        if (sortBy.column === columnName) {
            return sortBy.direction === 'asc' ?
                <i className="fa fa-sort-up ml-1" aria-hidden="true"></i> :
                <i className="fa fa-sort-down ml-1" aria-hidden="true"></i>;
        }
        return <i className="fa fa-sort ml-1" aria-hidden="true"></i>;
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Selection handlers
    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);

        if (checked) {
            const allIds = filteredAndSortedData
                .map(student => student.id);
            setSelectedStudents(allIds);
        } else {
            setSelectedStudents([]);
        }
    };

    // Auto-update selectAll when individual checkboxes change
    useEffect(() => {
        const eligibleIds = filteredAndSortedData
            .map(student => student.id);

        if (eligibleIds.length === 0) {
            setSelectAll(false);
            return;
        }

        const allEligibleSelected = eligibleIds.every(id => selectedStudents.includes(id));
        setSelectAll(allEligibleSelected);
    }, [selectedStudents, filteredAndSortedData, activeTab]);
    const handleSelectStudent = (studentId, applicationStatus) => {
        setSelectedStudents(prev => {
            if (prev.includes(studentId)) {
                return prev.filter(id => id !== studentId);
            } else {
                return [...prev, studentId];
            }
        });
    };

    // UNIFIED ACTION HANDLER - handles both single and bulk, forward and approve
    const handleUpdateStatus = async (creditIds, status, actionName) => {
        const idsArray = Array.isArray(creditIds) ? creditIds : [creditIds];

        if (idsArray.length === 0) {
            toast.error("Please select at least one student");
            return;
        }

        // Config per action
        const swalConfig = {
            forwarded_to_bos: {
                title: 'Forward to BOS?',
                text: idsArray.length > 1
                    ? `Are you sure you want to forward ${idsArray.length} selected student(s) to BOS?`
                    : 'Are you sure you want to forward this application to BOS?',
                icon: 'question',
                confirmButtonText: 'Yes, Forward',
                confirmButtonColor: '#198754',
            },
            application_approved: {
                title: 'Approve Application?',
                text: idsArray.length > 1
                    ? `Are you sure you want to approve ${idsArray.length} selected application(s)?`
                    : 'Are you sure you want to approve this application?',
                icon: 'question',
                confirmButtonText: 'Yes, Approve',
                confirmButtonColor: '#198754',
            },
            rejected_by_aca16: {
                title: 'Reject Application?',
                text: idsArray.length > 1
                    ? `Are you sure you want to reject ${idsArray.length} selected application(s)?`
                    : 'Are you sure you want to reject this application?',
                icon: 'warning',
                confirmButtonText: 'Yes, Reject',
                confirmButtonColor: '#dc3545',
            },
        };

        const config = swalConfig[status] || {
            title: 'Confirm Action?',
            text: `Are you sure you want to perform this action?`,
            icon: 'question',
            confirmButtonText: 'Yes, Proceed',
            confirmButtonColor: '#0d6efd',
        };

        const result = await Swal.fire({
            title: config.title,
            text: config.text,
            icon: config.icon,
            showCancelButton: true,
            confirmButtonColor: config.confirmButtonColor,
            cancelButtonColor: '#6c757d',
            confirmButtonText: config.confirmButtonText,
            cancelButtonText: 'Cancel',
            borderRadius: '10px',
            customClass: {
                popup: 'swal-custom-popup',
            }
        });

        if (!result.isConfirmed) return;

        setLoading(true);

        try {
            const response = await http.post('/College/updateExternalCreditStatus', {
                credit_ids: idsArray,
                status: status,
                remarks: ''
            });

            if (response.data.status) {
                await Swal.fire({
                    title: 'Done!',
                    text: response.data.message,
                    icon: 'success',
                    confirmButtonColor: '#198754',
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
                setSelectedStudents([]);
                setSelectAll(false);
                await showStudentList();
            } else {
                Swal.fire({
                    title: 'Failed',
                    text: response.data.message,
                    icon: 'error',
                    confirmButtonColor: '#dc3545',
                });
                setLoading(false);
            }
        } catch (error) {
            if (error.response?.data?.http_status === 403 && error.response.data.status_code === "ERR37") {
                Swal.fire({
                    title: 'Session Expired',
                    text: 'Your session has expired. Please log in again.',
                    icon: 'error',
                    confirmButtonColor: '#dc3545',
                });
                setTimeout(() => navigate("/admin/"), 3000);
            } else {
                Swal.fire({
                    title: 'Error',
                    text: error.response?.data?.message || `Error processing ${actionName.toLowerCase()}`,
                    icon: 'error',
                    confirmButtonColor: '#dc3545',
                });
            }
            setLoading(false);
        }
    };


    const handleTabChange = (tab) => {
        console.log("Switching to tab:", tab);
        setActiveTab(tab);
        setCurrentPage(1);
        setSelectedStudents([]);
        setSelectAll(false);
    };

    return (
        <>
            <ToastContainer />
            <Template>
                {loading ? (
                    <div className="container-fluid">
                        <div className="row justify-content-center align-items-center">
                            <div className="col-sm-12">
                                <div className="card h-100">
                                    <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                                        <div className="containeranimate">
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                            <div className="dot"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-sm-12">
                                <div className="card">
                                    <div className="col-md-12">
                                        <div className="card-header">
                                            <div className="py-3 d-flex justify-content-between align-items-center">
                                                <h5>External Credit Approval</h5>
                                            </div>
                                        </div>
                                        <div className="card-body pdg">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <label>Admission Year <span className="errorclr">*</span></label>
                                                        <Select
                                                            maxMenuHeight={200}
                                                            menuPlacement="auto"
                                                            id="Adimission_year"
                                                            name="Adimission_year"
                                                            className="form"
                                                            styles={customStyles}
                                                            options={[
                                                                { value: '2024', label: '2024' },
                                                                { value: '2025', label: '2025' },
                                                            ]}
                                                            value={selectedAdmissionYear}
                                                            onChange={handleAdmissionYearChange}
                                                        />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label>College <span className="errorclr">*</span></label>
                                                        <Select
                                                            maxMenuHeight={200}
                                                            menuPlacement="auto"
                                                            id="college_id"
                                                            name="college_id"
                                                            className="form"
                                                            styles={customStyles}
                                                            value={changedCollegeData}
                                                            options={getCollegesData && getCollegesData.map(college => ({
                                                                value: college.college_id,
                                                                label: college.college_name
                                                            }))}
                                                            onChange={handleCollegeChange}
                                                            isDisabled={!selectedAdmissionYearID}
                                                        />
                                                    </div>
                                                    <div className="col-md-3">
                                                        <label>Discipline <span className="errorclr">*</span></label>
                                                        <Select
                                                            maxMenuHeight={200}
                                                            menuPlacement="auto"
                                                            id="discipline_id"
                                                            name="discipline_id"
                                                            className="form"
                                                            styles={customStyles}
                                                            value={changedDisciplineData}
                                                            options={getDisciplinesData && getDisciplinesData.map(discipline => ({
                                                                value: discipline.discipline_id,
                                                                label: discipline.discipline_name
                                                            }))}
                                                            onChange={handleDisciplineChange}
                                                            isDisabled={!changedCollegesId}
                                                        />
                                                    </div>
                                                    <div className="col-md-2">
                                                        <div className="d-flex justify-content-end gap-2" style={{ marginTop: '28px' }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary action-btn"
                                                                onClick={showStudentList}
                                                            >
                                                                Go
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary clear-btn btn-sm"
                                                                onClick={handleClearFields}
                                                            >
                                                                Clear
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {studentData.length > 0 && (
                                                <div className="form theme-form">
                                                    <div className="custom-input">
                                                        <div className="card-body">
                                                            <div className="pdg1" style={{
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '10px',
                                                                overflow: 'hidden',
                                                                background: '#fff'
                                                            }}>
                                                                {/* <div style={{
                                                                    marginBottom: '12px',
                                                                    fontSize: '15px',
                                                                    fontWeight: '600',
                                                                    color: '#212529'
                                                                }}>
                                                                    Forwarded to BOS applications
                                                                </div> */}

                                                                {/* Table Content */}
                                                                <div style={{ padding: '16px', background: '#fff' }}>
                                                                    {/* Bulk action button */}


                                                                    <div className="table-responsive">
                                                                        <div className='row mt-2 mb-2'>
                                                                            <div className='col-md-6 wdt'>
                                                                                <p>
                                                                                    Showing {(currentPage - 1) * 50 + 1} - {Math.min((currentPage - 1) * 50 + 50, total_count)} of {total_count} entries
                                                                                </p>
                                                                            </div>
                                                                            <div className='col-md-6 wdt' align='right'>
                                                                                <input
                                                                                    type="text"
                                                                                    value={searchQuery}
                                                                                    onChange={handleSearchInputChange}
                                                                                    placeholder="Search..."
                                                                                    className='form-control'
                                                                                />
                                                                            </div>
                                                                        </div>

                                                                        {selectedStudents.length > 0 && (
                                                                            <div className="mb-3 d-flex gap-2 align-items-center flex-wrap">
                                                            

                                   
                                                                                    <>
                                                                                        <button
                                                                                            type="button"
                                                                                            style={{
                                                                                                background: '#fff',
                                                                                                border: '1px solid #198754',
                                                                                                color: '#198754',
                                                                                                borderRadius: '6px',
                                                                                                padding: '5px 12px',
                                                                                                fontSize: '13px',
                                                                                                cursor: 'pointer',
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                gap: '5px',
                                                                                                fontWeight: 500,
                                                                                                transition: 'all 0.15s ease'
                                                                                            }}
                                                                                            onMouseEnter={e => { e.currentTarget.style.background = '#198754'; e.currentTarget.style.color = '#fff'; }}
                                                                                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#198754'; }}
                                                                                            onClick={() => handleUpdateStatus(selectedStudents, 'application_approved', 'Bulk approved')}
                                                                                        >
                                                                                            <i className="bi bi-check-circle-fill" style={{ fontSize: '12px' }}></i>
                                                                                            Approve Selected ({selectedStudents.length})
                                                                                        </button>

                                                                                        <button
                                                                                            type="button"
                                                                                            style={{
                                                                                                background: '#fff',
                                                                                                border: '1px solid #dc3545',
                                                                                                color: '#dc3545',
                                                                                                borderRadius: '6px',
                                                                                                padding: '5px 12px',
                                                                                                fontSize: '13px',
                                                                                                cursor: 'pointer',
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                gap: '5px',
                                                                                                fontWeight: 500,
                                                                                                transition: 'all 0.15s ease'
                                                                                            }}
                                                                                            onMouseEnter={e => { e.currentTarget.style.background = '#dc3545'; e.currentTarget.style.color = '#fff'; }}
                                                                                            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#dc3545'; }}
                                                                                            onClick={() => handleUpdateStatus(selectedStudents, 'rejected_by_aca16', 'Bulk rejected')}
                                                                                        >
                                                                                            <i className="bi bi-x-circle-fill" style={{ fontSize: '12px' }}></i>
                                                                                            Reject Selected ({selectedStudents.length})
                                                                                        </button>
                                                                                    </>
                                                                               
                                                                            </div>
                                                                        )}

                                                                        <table className="display dataTable table-striped table-bordered ex-v-table" ref={tableRef}>
                                                                            <thead>
                                                                                <tr className="bg-primary">
                                                                                    <th style={{ width: '50px' }}>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={selectAll}
                                                                                            onChange={handleSelectAll}
                                                                                        />
                                                                                    </th>
                                                                                    <th>Sl No</th>
                                                                                    <th onClick={() => handleSort('studentname')} style={{ textAlign: "left", verticalAlign: "middle", cursor: 'pointer' }}>
                                                                                        Name / PRN {renderSortIcon('studentname')}
                                                                                    </th>
                                                                                    <th onClick={() => handleSort('course_name')} style={{ textAlign: "left", verticalAlign: "middle", cursor: 'pointer' }}>
                                                                                        Course Name {renderSortIcon('course_name')}
                                                                                    </th>
                                                                                    <th onClick={() => handleSort('acquired_credits')} style={{ textAlign: "center", verticalAlign: "middle", cursor: 'pointer' }}>
                                                                                        Credits {renderSortIcon('acquired_credits')}
                                                                                    </th>
                                                                                    <th style={{ textAlign: "center", verticalAlign: "middle" }}>Status</th>
                                                                                    <th style={{ textAlign: "center", verticalAlign: "middle" }}>Action</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {filteredAndSortedData && filteredAndSortedData.length > 0 ? (
                                                                                    filteredAndSortedData.map((item, index) => {
                                                                                        const serialNumber = (currentPage - 1) * 50 + index + 1;
                                                                                        const isSelected = selectedStudents.includes(item.id);
                                                                                        return (
                                                                                            <tr key={item.id}>
                                                                                                <td>
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        checked={isSelected}
                                                                                                        onChange={() => handleSelectStudent(item.id, item.application_status)}
                                                                                                    />
                                                                                                </td>
                                                                                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                                                                    {serialNumber}
                                                                                                </td>

                                                                                                {/* Name + PRN stacked */}
                                                                                                <td style={{ verticalAlign: "middle" }}>
                                                                                                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#212529' }}>
                                                                                                        {item.studentname}
                                                                                                    </div>
                                                                                                    <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '2px' }}>
                                                                                                        {item.prn}
                                                                                                    </div>
                                                                                                </td>

                                                                                                {/* Course Name */}
                                                                                                <td style={{ verticalAlign: "middle", fontSize: '13px' }}>
                                                                                                    {item.course_name}
                                                                                                </td>

                                                                                                {/* Credits */}
                                                                                                <td style={{ textAlign: "center", verticalAlign: "middle", fontWeight: 600, fontSize: '13px' }}>
                                                                                                    {item.acquired_credits}
                                                                                                </td>

                                                                                                {/* Status */}
                                                                                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                                                                    {(() => {
                                                                                                        const statusConfig = {
                                                                                                            forwarded_to_university: { label: 'Principal Approved', bg: '#0d6efd' },
                                                                                                            forwarded_to_bos: { label: 'BOS Approval Pending', bg: '#6f42c1' },
                                                                                                            bos_course_approved: { label: 'BOS Approved', bg: '#fd7e14' },
                                                                                                            application_approved: { label: 'Approved', bg: '#28a745' },
                                                                                                            rejected_by_aca16: { label: 'Rejected', bg: '#dc3545' },
                                                                                                        };
                                                                                                        const cfg = statusConfig[item.application_status] || { label: item.application_status_label || item.application_status, bg: '#6c757d' };
                                                                                                        return (
                                                                                                            <span style={{
                                                                                                                background: cfg.bg,
                                                                                                                color: '#fff',
                                                                                                                borderRadius: '20px',
                                                                                                                padding: '3px 10px',
                                                                                                                fontSize: '11px',
                                                                                                                fontWeight: 600,
                                                                                                                whiteSpace: 'nowrap'
                                                                                                            }}>
                                                                                                                {cfg.label}
                                                                                                            </span>
                                                                                                        );
                                                                                                    })()}
                                                                                                </td>

                                                                                                {/* Action */}
                                                                                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                                                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>

                                                                                                        {/* View */}
                                                                                                        <ActionBtn
                                                                                                            onClick={() => openViewModal(item)}
                                                                                                            title="View Application"
                                                                                                            borderColor="#0dcaf0"
                                                                                                            icon="bi bi-eye-fill"
                                                                                                        />

                                                                                                        {/* Approve + Reject */}
                                                                                                        <ActionBtn
                                                                                                            onClick={() => handleUpdateStatus(item.id, 'bos_course_approved', 'Application Approved')}
                                                                                                            title="Approve"
                                                                                                            borderColor="#198754"
                                                                                                            icon="bi bi-check-lg"
                                                                                                        />
                                                                                                        <ActionBtn
                                                                                                            onClick={() => handleUpdateStatus(item.id, 'bos_course_rejected', 'Rejected')}
                                                                                                            title="Reject"
                                                                                                            borderColor="#dc3545"
                                                                                                            icon="bi bi-x-lg"
                                                                                                        />

                                                                                                    </div>
                                                                                                </td>
                                                                                            </tr>
                                                                                        );
                                                                                    })
                                                                                ) : (
                                                                                    <tr>
                                                                                        <td colSpan="11" className="text-center py-3">No Data Found</td>
                                                                                    </tr>
                                                                                )}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>

                                                                    {/* Pagination */}
                                                                    {totalPages > 1 && (
                                                                        <nav aria-label="..." style={{ cursor: "pointer" }}>
                                                                            <ul className="pagination justify-content-center mt-3">
                                                                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                                    <span className="page-link pgn-clr" onClick={handlePreviousPage}>
                                                                                        <i className="bi bi-arrow-left-circle-fill"></i>
                                                                                    </span>
                                                                                </li>
                                                                                <li className="page-item">
                                                                                    <span className="page-link pgn-clr" onClick={() => handlePageChange(1)}>1</span>
                                                                                </li>
                                                                                {currentPage > 4 && <li className="page-item"><span className="page-link pgn-clr">...</span></li>}
                                                                                {[...Array(totalPages - 1).keys()].slice(currentPage > 4 ? currentPage - 3 : 1, currentPage + 2).map((pageNumber) => (
                                                                                    <li key={pageNumber} className={`page-item ${pageNumber + 1 === currentPage ? 'act-clr' : ''}`}>
                                                                                        <span className="page-link pgn-clr" onClick={() => handlePageChange(pageNumber + 1)}>
                                                                                            {pageNumber + 1}
                                                                                        </span>
                                                                                    </li>
                                                                                ))}
                                                                                {currentPage < totalPages - 3 && <li className="page-item"><span className="page-link pgn-clr">...</span></li>}
                                                                                {totalPages > 1 && (
                                                                                    <li className="page-item">
                                                                                        <span className="page-link pgn-clr" onClick={() => handlePageChange(totalPages)}>{totalPages}</span>
                                                                                    </li>
                                                                                )}
                                                                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                                    <span className="page-link pgn-clr" onClick={handleNextPage}>
                                                                                        <i className="bi bi-arrow-right-circle-fill"></i>
                                                                                    </span>
                                                                                </li>
                                                                            </ul>
                                                                        </nav>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <ViewApplicationModal
                    showViewModal={showViewModal}
                    closeViewModal={closeViewModal}
                    viewData={viewData}
                    viewLoading={viewLoading}
                    pdfLoading={pdfLoading}
                    pdfModal={pdfModal}
                    openPdfModal={openPdfModal}
                    closePdfModal={closePdfModal}
                />
            </Template> 

            <PDFModal
                isOpen={pdfModal.show}
                onClose={closePdfModal}
                pdfUrl={pdfModal.url}
                fileName={pdfModal.name}
                fileType={pdfModal.type}
            />
        </>
    );
}